import React from 'react';

interface PullToRefreshIndicatorProps {
  /**
   * Current pull distance
   */
  pullDistance: number;
  
  /**
   * Whether the pull threshold has been exceeded
   */
  thresholdExceeded: boolean;
  
  /**
   * Whether a refresh is currently in progress
   */
  isRefreshing: boolean;
  
  /**
   * Ref to attach to the indicator element
   */
  indicatorRef: React.RefObject<HTMLDivElement>;
}

/**
 * A component that displays the pull-to-refresh indicator
 */
export function PullToRefreshIndicator({
  pullDistance,
  thresholdExceeded,
  isRefreshing,
  indicatorRef,
}: PullToRefreshIndicatorProps): JSX.Element {
  // Only show if pulling or refreshing
  const isVisible = pullDistance > 0 || isRefreshing;
  
  // Calculate opacity based on pull distance (max 60px for full opacity)
  const opacity = Math.min(pullDistance / 60, 1);
  
  // Calculate rotation based on pull distance
  const rotation = (pullDistance / 20) * 180; // Turn 180 degrees every 20px
  
  return (
    <div
      ref={indicatorRef}
      className="absolute left-0 right-0 flex justify-center z-10 pointer-events-none transition-transform"
      style={{
        height: '60px',
        transform: isRefreshing ? 'translateY(0)' : `translateY(${Math.max(0, pullDistance - 60)}px)`,
        top: isRefreshing ? '0' : '-60px',
        opacity: isVisible ? 1 : 0,
      }}
      aria-hidden="true"
    >
      <div className="px-4 py-3 rounded-full bg-surface shadow-md flex items-center gap-3 mt-3">
        {isRefreshing ? (
          // Show spinner when refreshing
          <svg 
            className="animate-spin h-5 w-5 text-primary" 
            xmlns="http://www.w3.org/2000/svg" 
            fill="none" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4"
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          // Show arrow icon when pulling
          <svg 
            className="h-5 w-5 text-primary transition-transform duration-200"
            style={{ 
              transform: `rotate(${rotation}deg)`,
              opacity: opacity
            }}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
            aria-hidden="true"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </svg>
        )}
        <span 
          className="text-sm font-medium text-text-secondary"
          style={{ opacity: opacity }}
        >
          {isRefreshing 
            ? 'Refreshing...' 
            : thresholdExceeded 
              ? 'Release to refresh' 
              : 'Pull to refresh'}
        </span>
      </div>
    </div>
  );
} 